/**
 * Compact summary chips rendered in the compare-sales dialog title bar
 * (warnings / differences). Lives in components/inspector/shared so the
 * dialog and the view can both import without crossing the pages/ ↔
 * components/ boundary.
 *
 * Tone palette is shared with the surrounding panel by colour token; the
 * chip itself is small enough that callers can drop it inline beside
 * other title-bar controls.
 */
import React from 'react'
import { Box, Chip, Stack } from '@mui/material'
import WarningAmberIcon from '@mui/icons-material/WarningAmber'
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline'
import { SaleMismatch } from '../../../utils/saleMismatchIssues'
import { COMPARE_COLORS } from './compareSalesPalette'
import { TooltipChip } from './TooltipChip'

export type SummaryTone = 'success' | 'warning' | 'error' | 'neutral'

const SUMMARY_CHIP_TONES: Record<SummaryTone, { fg: string; border: string; bg: string }> = {
  success: { fg: '#2E7D32', border: '#A5D6A7', bg: 'transparent' },
  warning: { fg: '#B26A00', border: '#F2C28A', bg: 'transparent' },
  error: { fg: COMPARE_COLORS.mismatchBorder, border: COMPARE_COLORS.mismatchBorder, bg: 'transparent' },
  neutral: { fg: 'text.secondary', border: 'rgba(0,0,0,0.18)', bg: 'transparent' },
}

export const SummaryChip: React.FC<{
  icon: React.ReactNode
  label: string
  tone: SummaryTone
  emphasised?: boolean
}> = ({ icon, label, tone, emphasised }) => {
  const palette = SUMMARY_CHIP_TONES[tone]
  return (
    <Chip
      size="small"
      icon={icon as React.ReactElement}
      label={label}
      sx={{
        height: 22,
        fontSize: '0.75rem',
        fontWeight: emphasised ? 600 : 500,
        color: palette.fg,
        backgroundColor: emphasised ? COMPARE_COLORS.mismatchBg : palette.bg,
        border: '1px solid',
        borderColor: palette.border,
        '& .MuiChip-icon': { color: palette.fg, ml: 0.5 },
        '& .MuiChip-label': { px: 0.75 },
      }}
    />
  )
}

/**
 * Compact 2-chip summary rendered in the dialog's title bar - warnings
 * and sale-level differences. The "N matched" chip used to live here too
 * but it was low-signal noise (every clean sale shows it), so it was
 * dropped.
 */
export const HeaderSummaryChips: React.FC<{
  warnings: number
  mismatches: SaleMismatch[]
}> = ({ warnings, mismatches }) => {
  const differences = mismatches.length

  const warningsTooltip = warnings > 0
    ? `${warnings} line item${warnings === 1 ? '' : 's'} with non-blocking differences (qty / description / $0 / rounding / note)`
    : ''
  const differencesTooltip = differences > 0 ? (
    <Box sx={{ py: 0.25 }}>
      <Box sx={{ fontWeight: 600, mb: 0.5 }}>
        {differences} sale-level difference{differences === 1 ? '' : 's'}
      </Box>
      <Box component="ul" sx={{ m: 0, pl: 2 }}>
        {mismatches.map(m => (
          <Box component="li" key={m.key}>{m.label}</Box>
        ))}
      </Box>
    </Box>
  ) : ''

  return (
    <Stack direction="row" spacing={0.75} alignItems="center" flexWrap="wrap" useFlexGap>
      {warnings > 0 && (
        <TooltipChip title={warningsTooltip}>
          <SummaryChip
            icon={<WarningAmberIcon sx={{ fontSize: 14 }} />}
            label={`${warnings} warning${warnings === 1 ? '' : 's'}`}
            tone="warning"
          />
        </TooltipChip>
      )}
      {differences > 0 && (
        <TooltipChip title={differencesTooltip}>
          <SummaryChip
            icon={<ErrorOutlineIcon sx={{ fontSize: 14 }} />}
            label={`${differences} difference${differences === 1 ? '' : 's'}`}
            tone="error"
          />
        </TooltipChip>
      )}
    </Stack>
  )
}
